import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { DataGridPro, GridRowModes } from '@mui/x-data-grid-pro';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { saveConfirmationResult } from '../../../../../../packages/common/api';
import ArrowDownIcon from '../../../../../../packages/common/shared-ui/src/icons/ArrowDownIcon';
import ChevronIcon from '../../../../../../packages/common/shared-ui/src/icons/ChevronIcon';
import SortIcon from '../../../../../../packages/common/shared-ui/src/icons/SortIcon';
import { useTranslation } from '../../../../../../packages/common/utils/getLocalString';

// Original class: selectLabel
const StyledSelectLabel = styled(InputLabel, {
  shouldForwardProp: prop => !['paddingStyle'].includes(prop),
})(({ theme, paddingStyle }) => ({
  '&.MuiFormLabel-root.MuiInputLabel-root': {
    color: '#7174AC',
    fontSize: '14px',
    lineHeight: '20px',
    transform: 'none',
    ...(paddingStyle && {
      left: '10px',
      top: '10px',
    }),
    '&.Mui-focused': {
      transform: 'none',
    },
    '&.MuiFormLabel-filled': {
      display: 'none',
    },
  },
}));
// Original class: rowSelect
const StyledRowSelect = styled(Select, {
  shouldForwardProp: prop => !['paddingStyle'].includes(prop),
})(({ theme, paddingStyle }) => ({
  '&.MuiInputBase-root.MuiInput-root': {
    marginTop: 0,
    width: '100%',
    fontSize: '14px',
    lineHeight: '20px',
    height: '100%',
    padding: '0',
    color: '#7174AC',
    '&.changed': {
      color: '#212346',
    },
    '&::before': {
      borderBottom: 'none',
    },
    '&:hover': {
      '&::before': {
        borderBottom: 'none',
      },
    },
    '& .MuiSelect-select': {
      padding: 0,
      ...(paddingStyle && {
        padding: '10px 10px',
        height: '100%',
        boxSizing: 'border-box',
      }),
      '&:focus': {
        backgroundColor: 'transparent',
      },
    },
    '& .MuiSvgIcon-root.MuiSelect-icon': {
      width: '20px',
      height: '20px',
      position: 'static',
      ...(paddingStyle && {
        position: 'absolute',
        right: '10px',
      }),
    },
    '& .MuiSelect-nativeInput': {
      padding: '0',
    },
    '&.Mui-error': {
      border: '1px solid #E03737',
      '&::after': {
        display: 'none',
      },
    },
  },
}));
// Original class: rowInput
const StyledRowInput = styled(TextField)(theme => ({
  '& .MuiInputBase-root.MuiInput-root': {
    fontSize: '14px',
    lineHeight: '20px',
    '&::before': {
      borderBottom: 'none',
    },
    '&:hover': {
      '&::before': {
        borderBottom: 'none',
      },
    },
    '& .MuiInputBase-input.MuiInput-input': {
      color: '#7174AC',
      padding: 0,
      '&::placeholder': {
        color: '#7174AC',
        opacity: 1,
      },
    },
    '&.Mui-error': {
      border: '1px solid #E03737',
      '&::after': {
        display: 'none',
      },
    },
  },

  '&.changed': {
    '& .MuiInputBase-root.MuiInput-root': {
      '& .MuiInputBase-input.MuiInput-input': {
        color: '#212346',
      },
    },
  },
}));
// Original class: paperDropdown
const StyledPaperDropdown = styled(Paper)(theme => ({
  '&.MuiPaper-root.MuiPopover-paper.MuiMenu-paper': {
    position: 'absolute',
    overflowY: 'auto',
    overflowX: 'hidden',
    maxHeight: '300px',
    borderRadius: '0 0 16px 16px',
    borderTop: '1px solid #E4E4EF',
    boxShadow: '0px 5px 10px -8px #A0A1AA66',
    '&::-webkit-scrollbar': {
      width: '2px',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#7174AC',
      borderRadius: '5px',
    },
    '& .MuiList-root.MuiMenu-list': {
      padding: '0',
      '& li': {
        fontSize: '14px',
        lineHeight: '20px',
        color: '#212346',
        '&.MuiButtonBase-root.MuiMenuItem-root': {
          '&:hover': {
            backgroundColor: '#DCDFF4',
          },
        },
      },
    },
  },
}));
// Original class: DataGridTableRoot
const StyledDataGridTableRoot = styled(DataGridPro)(({ theme }) => ({
  width: '100%',
  overflow: 'hidden',

  '&.MuiDataGrid-root': {
    border: 0,
    '& .MuiDataGrid-columnHeaders': {
      borderBottom: '1px solid #E4E4EF',
      '& .MuiBadge-badge': {
        display: 'none',
      },
      '& .MuiDataGrid-columnHeader.MuiDataGrid-withBorderColor': {
        '&.tableGroupGanttHeaderCell': {},
        '&:not(.MuiDataGrid-columnHeaderCheckbox)': {
          '& + .MuiDataGrid-columnHeader.MuiDataGrid-withBorderColor': {
            borderLeft: '1px solid #E4E4EF',
            '&.actions': {
              borderLeft: 'none',
            },
          },
        },
      },
      '& .MuiDataGrid-columnHeader.MuiDataGrid-withBorderColor.MuiDataGrid-columnHeaderCheckbox':
        {
          minWidth: '36px!important',
          maxWidth: '36px!important',
          '& + .MuiDataGrid-columnHeader.MuiDataGrid-withBorderColor': {
            paddingLeft: 0,
          },
        },
    },
    '& .MuiDataGrid-virtualScroller': {
      '&::-webkit-scrollbar': {
        height: '6px',
        width: '6px',
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: '#7174AC',
        borderRadius: '5px',
      },
    },
    '& .MuiDataGrid-row': {
      width: '100%',
      borderBottom: '1px solid #E4E4EF',
      '& .MuiDataGrid-rowReorderCellContainer': {
        borderRight: '1px solid #E4E4EF',
      },
      '&.errorRow': {
        outline: '1px solid #E03737',
      },
      '& .MuiDataGrid-cell': {
        '&.tableCell': {
          overflow: 'visible',
          '&.parent': {
            paddingLeft: 0,
          },
          '&:not(.MuiDataGrid-columnHeaderCheckbox)': {
            '& + .MuiDataGrid-cell.tableCell': {
              borderLeft: '1px solid #E4E4EF',
            },
          },
          '&.tableGroupCell': {
            overflow: 'visible',
            whiteSpace: 'nowrap',
            '&:not(.MuiDataGrid-columnHeaderCheckbox)': {
              '& + .MuiDataGrid-cell.tableCell': {
                borderLeft: '1px solid transparent',
              },
            },
          },
          '&.tableGroupGanttCell': {
            padding: '10px 10px 10px 0',
            borderLeft: '1px solid #E4E4EF',
          },
        },
      },
      '& .MuiDataGrid-cell.MuiDataGrid-cellCheckbox': {
        minWidth: '36px!important',
        maxWidth: '36px!important',
        '& + .MuiDataGrid-cell': {
          paddingLeft: 0,
        },
      },
      '&.MuiDataGrid-row--editing': {
        boxShadow: 'none',
      },
    },
    '& .MuiDataGrid-row.groupRow': {
      '&.confirmationRow': {
        borderTop: '4px solid #E4E4EF',
        '&.first-row': {
          borderTop: 'none',
        },
      },
      '& .MuiDataGrid-cellCheckbox': {
        boxShadow: 'none',
        '& .MuiCheckbox-root': {
          visibility: 'hidden',
        },
      },
    },
    '& .MuiDataGrid-row .MuiDataGrid-cell:last-child': {
      borderRight: 0,
    },
    '& .MuiDataGrid-row.Mui-selected': {
      backgroundColor: '#DCDFF4',
    },
    '& .MuiDataGrid-row.Mui-selected.Mui-hovered': {
      backgroundColor: '#DCDFF4',
    },
    '& .MuiDataGrid-main .MuiDataGrid-iconButtonContainer .MuiButtonBase-root':
      {
        marginLeft: theme.spacing(2),
      },
    '& .MuiCheckbox-root': {
      '& .MuiSvgIcon-root': {
        width: '12px',
      },
    },
    '& .MuiDataGrid-columnSeparator': {
      '& .MuiSvgIcon-root': {
        width: '24px',
      },
    },
    '& .MuiDataGrid-cell': {
      borderBottom: 'none',
      '&.MuiDataGrid-cell--editing': {
        boxShadow: 'none',
        '&:focus-within': {
          outline: 'none',
        },
      },
      '&.tableCell': {
        wordBreak: 'break-word',
        borderColor: 'transparent',
        position: 'relative',
        padding: '10px',
        lineHeight: '20px',
        '&.tableSelectCell': {
          overflow: 'visible',
        },
        '& a': {
          position: 'relative',
        },
        '&:last-child': {
          boxShadow: 0,
        },
        '& .MuiCheckbox-root': {
          padding: 0,
        },
        '&.cellEdit': {
          padding: 0,
        },
        '&.selectCell': {
          padding: 0,
        },
      },
      '&.actions': {
        padding: 0,
        '& .MuiDataGrid-actionsCell': {
          height: '100%',
          width: '100%',
          gridGap: 0,
        },
        '& .MuiButtonBase-root.MuiIconButton-root': {
          height: '100%',
          borderRadius: '0',
          padding: '0',
          flex: 1,
          '& .MuiSvgIcon-root': {
            width: '16px',
            height: '16px',
          },
          '& + .MuiButtonBase-root.MuiIconButton-root': {
            borderLeft: '1px solid #E4E4EF',
          },
        },
      },
      '& a': {
        '&:hover': {
          textDecoration: 'underline',
        },
      },
    },
    '& .MuiDataGrid-cellCheckbox': {
      borderColor: 'transparent',
    },
    '& .MuiDataGrid-main': {
      borderRadius: '16px',
      backgroundColor: '#fff',
    },
  },

  '& .MuiDataGrid-footerContainer': {
    marginTop: '20px',
    minHeight: 'auto',
    border: 'none',
    justifyContent: 'flex-start',
    '& .MuiToolbar-root.MuiTablePagination-toolbar': {
      minHeight: 'auto',
      padding: 0,
      '& .MuiPagination-root': {
        marginLeft: 0,
        '& li': {
          fontSize: '16px',
          lineHeight: '20px',
        },
        '& li + li': {
          marginLeft: '2px',
        },
        '& .MuiButtonBase-root.MuiPaginationItem-root': {
          color: '#41424E',
          fontSize: '16px',
          lineHeight: '20px',
          borderRadius: '4px',
          minWidth: '28px',
          height: '28px',
          margin: 0,
          '&.Mui-selected': {
            backgroundColor: '#6270D0',
            color: '#FAFAFC',
            '&:hover': {
              color: '#FAFAFC',
              backgroundColor: '#6270D0',
            },
          },
          '& .MuiSvgIcon-root.MuiPaginationItem-icon': {
            width: 'auto',
            height: 'auto',
          },
          '&:hover': {
            color: '#6270D0',
            backgroundColor: 'transparent',
          },
        },
        '& .MuiPaginationItem-ellipsis': {
          fontSize: '16px',
          lineHeight: '20px',
          color: '#41424E',
          padding: 0,
          margin: 0,
          minWidth: ' 28px',
        },
      },
    },
  },
}));
const ConfirmationResultTable = ({
  confirmationResult,
  confirmationCanceled,
}) => {
  const getLocalString = useTranslation();
  const componentLocalization = useSelector(
    state =>
      state.localization?.components?.['ConfirmationPage']?.sections?.result,
  );
  const pageParams = useParams();
  const [confirmationResultColumns, setConfirmationResultColumns] = useState(
    [],
  );
  const [confirmationResultRows, setConfirmationResultRows] = useState([]);
  const [rowModesModel, setRowModesModel] = useState({});
  const [error, setError] = useState(null);
  const [showSaveButton, setShowSaveButton] = useState(null);

  const formattedGridRows = useCallback(
    data =>
      data.reduce((acc, row, index) => {
        const newCols = row.columns.reduce((columnAcc, item, i) => {
          let newItem = {
            ...columnAcc,
            [row.columns[i].fieldId]: row.columns[i].valueName,
            [row.columns[i].fieldId + 'Id']: row.columns[i].valueId,
          };
          if (item.fieldDefinition && item.fieldDefinition.values) {
            newItem.values = item.fieldDefinition.values;
          }
          return newItem;
        }, {});
        delete row.columns;
        return [...acc, { ...newCols, ...row, id: row.rowId }];
      }, []),
    [],
  );

  useEffect(() => {
    if (confirmationCanceled) {
      setShowSaveButton(false);
      if (Object.keys(rowModesModel).length > 0) {
        setRowModesModel(oldModel => {
          const firstKey = Object.keys(oldModel)[0];
          if (!firstKey) return oldModel;

          return {
            ...oldModel,
            [firstKey]: {
              ...oldModel[firstKey],
              mode: GridRowModes.View,
            },
          };
        });
      }
    }
  }, [confirmationCanceled]);

  useEffect(() => {
    if (confirmationResult) {
      confirmationResult.columns &&
        setConfirmationResultColumns(confirmationResult.columns);
      if (confirmationResult.rows) {
        setConfirmationResultRows(formattedGridRows(confirmationResult.rows));
        const editableRow = confirmationResult.rows.find(
          item => item.services.canEdit,
        );
        if (editableRow !== undefined) {
          setShowSaveButton(true);
          setRowModesModel(oldModel => ({
            ...oldModel,
            [editableRow.rowId]: {
              mode: GridRowModes.Edit,
            },
          }));
        }
      }
    }
  }, [confirmationResult]);

  const handleSelect = useCallback(
    (event, id) => {
      const index = confirmationResultRows.findIndex(
        item => Number(item.id) === Number(id),
      );
      const updatedObject = {
        ...confirmationResultRows[index],
        ['confirmation.resultId']: event.target.value,
      };
      setConfirmationResultRows([
        ...confirmationResultRows.slice(0, index),
        updatedObject,
        ...confirmationResultRows.slice(index + 1),
      ]);
    },
    [confirmationResultRows],
  );
  const handleTextField = useCallback(
    (event, id) => {
      const index = confirmationResultRows.findIndex(item => item.id === id);

      const updatedObject = {
        ...confirmationResultRows[index],
        ['confirmation.comment']: event.target.value,
      };
      setConfirmationResultRows([
        ...confirmationResultRows.slice(0, index),
        updatedObject,
        ...confirmationResultRows.slice(index + 1),
      ]);
    },
    [confirmationResultRows],
  );
  const formattedGridColumns = useCallback(() => {
    const smallFields = ['date', 'bool'];
    const editableColumns = ['confirmation.result', 'confirmation.comment'];
    const columns = confirmationResultColumns.map(column => {
      return {
        field: column.fieldId,
        headerName: column.fieldName,
        renderHeader: props => (
          <Typography
            variant='h4'
            sx={{
              fontWeight: 700,
            }}
          >
            {props.colDef.headerName}
          </Typography>
        ),
        disableExport: false,
        flex: smallFields.includes(column.fieldType) ? 0 : 1,
        width: smallFields.includes(column.fieldType) && 150,
        cellClassName: props =>
          props.field === 'confirmation.result' && props.cellMode === 'edit'
            ? 'tableCell selectCell'
            : 'tableCell',
        editable:
          !confirmationCanceled && editableColumns.includes(column.fieldId),
        renderEditCell: props => {
          if (props.field === 'confirmation.result') {
            return (
              <FormControl sx={{ width: '100%', height: '100%' }}>
                <StyledSelectLabel paddingStyle={true} id='select-label'>
                  {getLocalString('labels', 'select')}
                </StyledSelectLabel>
                <StyledRowSelect
                  paddingStyle={true}
                  native={false}
                  labelId='select-label'
                  label={getLocalString('labels', 'select')}
                  variant='standard'
                  sx={{ width: '100%', border: 'none', padding: '0 10px' }}
                  MenuProps={{ slots: { paper: StyledPaperDropdown } }}
                  IconComponent={props => (
                    <ChevronIcon direction={'down'} {...props} />
                  )}
                  value={
                    props.row['confirmation.resultId'] !== '0'
                      ? props.row['confirmation.resultId']
                      : ''
                  }
                  onChange={event => handleSelect(event, props.id)}
                >
                  {props.row.values
                    .filter(item => item.valueId !== '0')
                    .map((item, index) => (
                      <MenuItem key={index} value={item.valueId}>
                        {item.valueName}
                      </MenuItem>
                    ))}
                </StyledRowSelect>
              </FormControl>
            );
          } else {
            return (
              <StyledRowInput
                autoFocus
                placeholder={getLocalString('labels', 'input')}
                variant='standard'
                onChange={event => handleTextField(event, props.row.id)}
              ></StyledRowInput>
            );
          }
        },
        renderCell: props => {
          const stringValue = props.value;
          return stringValue ? stringValue : '';
        },
      };
    });
    return [...columns];
  }, [
    confirmationResultColumns,
    getLocalString,
    handleSelect,
    handleTextField,
  ]);
  const contractStagesColumnsValue = formattedGridColumns();

  const handleCellDoubleClick = useCallback(event => {
    event.stopPropagation();
  }, []);

  const validateRow = useCallback(
    (index, rowToSave) => {
      let isError;
      let updatedObject;
      if (
        rowToSave['confirmation.resultId'] === '2' &&
        !rowToSave['confirmation.comment']
      ) {
        isError = true;
        isError && setError([componentLocalization?.errors.saveError]);
      }
      if (rowToSave['confirmation.resultId'] === '0') {
        isError = true;
        isError && setError([componentLocalization?.errors.saveResultError]);
      }
      if (isError) {
        updatedObject = {
          ...confirmationResultRows[index],
          error: true,
        };
        setConfirmationResultRows([
          ...confirmationResultRows.slice(0, index),
          updatedObject,
          ...confirmationResultRows.slice(index + 1),
        ]);
        return false;
      } else {
        updatedObject = { ...confirmationResultRows[index] };
        delete updatedObject.error;
        setConfirmationResultRows([
          ...confirmationResultRows.slice(0, index),
          updatedObject,
          ...confirmationResultRows.slice(index + 1),
        ]);
        setError('');
        return true;
      }
    },
    [componentLocalization, confirmationResultRows],
  );

  const handleSaveClick = useCallback(async () => {
    const rowToSave = confirmationResultRows.find(
      item => item.services.canEdit,
    );
    const index = confirmationResultRows.findIndex(
      item => Number(item.id) === Number(rowToSave.id),
    );
    const validation = validateRow(index, rowToSave);
    if (!validation) {
      return;
    }
    try {
      const data = {
        result: Number(rowToSave['confirmation.resultId']),
        comment: rowToSave['confirmation.comment'],
      };
      const response = await saveConfirmationResult(
        pageParams.id,
        rowToSave.id,
        data,
      );
      if (response) {
        const { data: res } = response;
        //const formattedRes = formattedGridRows([res]);
        const newCols = res.columns.reduce((columnAcc, item, i) => {
          let newItem = {
            ...columnAcc,
            [res.columns[i].fieldId]: res.columns[i].valueName,
            [res.columns[i].fieldId + 'Id']: res.columns[i].valueId,
          };
          if (item.fieldDefinition && item.fieldDefinition.values) {
            newItem.values = item.fieldDefinition.values;
          }
          return newItem;
        }, {});
        delete res.columns;
        const updatedObject = {
          ...res,
          ...newCols,
          id: res.rowId,
        };
        setConfirmationResultRows([
          ...confirmationResultRows.slice(0, index),
          updatedObject,
          ...confirmationResultRows.slice(index + 1),
        ]);
        setRowModesModel({
          ...rowModesModel,
          [res.rowId]: { mode: GridRowModes.View, ignoreModifications: true },
        });
        setShowSaveButton(false);
      }
    } catch (error) {
      console.error('ERROR WITH GET', error);
    }
  }, [confirmationResultRows, pageParams.id, rowModesModel, validateRow]);

  return (
    <>
      {confirmationResultColumns && confirmationResultRows && (
        <Grid
          container
          sx={{
            flexDirection: 'column',
            position: 'relative',
          }}
        >
          {error && (
            <Box sx={{ position: 'absolute', top: '-20px', left: '200px' }}>
              <Typography
                variant='h3'
                sx={{
                  color: '#E03737',
                }}
              >
                {error}
              </Typography>
            </Box>
          )}
          <Grid
            sx={{
              borderRadius: '16px',
              marginTop: '8px',
              overflow: 'hidden',
              width: '100%',
              position: 'relative',
            }}
          >
            <StyledDataGridTableRoot
              localeText={{
                noRowsLabel: getLocalString('labels', 'noData'),
              }}
              onRowDoubleClick={(params, event) => handleCellDoubleClick(event)}
              onCellDoubleClick={(params, event) =>
                handleCellDoubleClick(event)
              }
              autoHeight
              hideFooter
              disableRowSelectionOnClick
              disableColumnMenu
              columns={contractStagesColumnsValue}
              rows={confirmationResultRows}
              getRowClassName={params => params.row.groupName && 'groupRow'}
              pageSize={10}
              slots={{
                columnUnsortedIcon: () => (
                  <SortIcon width='20' height='20' viewBox='0 0 20 20' />
                ),
                columnSortedAscendingIcon: () => (
                  <SortIcon
                    direction='up'
                    width='20'
                    height='20'
                    viewBox='0 0 20 20'
                  />
                ),
                columnSortedDescendingIcon: () => (
                  <SortIcon
                    direction='down'
                    width='20'
                    height='20'
                    viewBox='0 0 20 20'
                  />
                ),
              }}
              rowHeight={40}
              editMode='row'
              rowModesModel={rowModesModel}
            />
          </Grid>
          {showSaveButton && (
            <Grid>
              <Button
                onClick={handleSaveClick}
                variant='defaultBlueSecondary'
                disableRipple
                sx={{ marginTop: '8px' }}
              >
                <Typography
                  variant='h4'
                  sx={{
                    fontWeight: 700,
                  }}
                >
                  {getLocalString('buttons', 'save')}
                </Typography>
              </Button>
            </Grid>
          )}
        </Grid>
      )}
    </>
  );
};

export default ConfirmationResultTable;
